// eTAMS — New travel request (PDF page 3). Also edits drafts / returned (?id=)
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { etamsAPI, wsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Card, Field, Input, Select, VFlow, Note, Loading, Spin,
  useAsync, useProjects, useQueryParam, money, isoDate, BUSINESS_UNITS,
} from '../../components/ui';
import { notify } from '../../utils/notify';
import { MODES_OF_TRAVEL, policyCaps } from '../../utils/etams';

const COSTS = [['fare', 'Fare'], ['hotel', 'Hotel'], ['food', 'Food'], ['ope', 'OPE'], ['conveyance', 'Conveyance'], ['other', 'Other']];
const empty = (u) => ({
  businessUnit: u?.businessUnit || 'Water Business Unit', projectCode: u?.defaultProject || '', policyBand: u?.policyBand || '', cityTier: 'C',
  origin: u?.baseLocation || '', destination: '', departureDate: '', returnDate: '', modeOfTravel: MODES_OF_TRAVEL[0], purpose: '',
  costs: { fare: '', hotel: '', food: '', ope: '', conveyance: '', other: '' },
});

export default function NewTravelRequest() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const id = useQueryParam('id');
  const projects = useProjects();
  const policy = useAsync(() => etamsAPI.policy().then((d) => d.policy), [], { silent: true });
  const me = useAsync(() => wsAPI.profile().then((d) => d.profile), [], { silent: true });
  const existing = useAsync(() => (id ? etamsAPI.getTravel(id).then((d) => d.request) : Promise.resolve(null)), [id]);
  const [f, setF] = useState(empty(user));
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (me.data && !id) {
      setF((p) => ({
        ...p,
        businessUnit: me.data.businessUnit || p.businessUnit,
        projectCode: p.projectCode || me.data.defaultProject || '',
        policyBand: p.policyBand || me.data.policyBand || '',
        origin: p.origin || me.data.baseLocation || '',
      }));
    }
  }, [me.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = existing.data;
    if (!t) return;
    setF({
      businessUnit: t.businessUnit || '', projectCode: t.projectCode || '', policyBand: t.policyBand || '', cityTier: t.cityTier || 'C',
      origin: t.origin || '', destination: t.destination || '', departureDate: isoDate(t.departureDate), returnDate: isoDate(t.returnDate),
      modeOfTravel: t.modeOfTravel || MODES_OF_TRAVEL[0], purpose: t.purpose || '',
      costs: Object.fromEntries(COSTS.map(([k]) => [k, t.costs?.[k] ?? ''])),
    });
  }, [existing.data]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setCost = (k, v) => setF((p) => ({ ...p, costs: { ...p.costs, [k]: v } }));
  const total = COSTS.reduce((s, [k]) => s + (Number(f.costs[k]) || 0), 0);
  const fraction = policy.data?.advanceCeilingFraction ?? 1 / 3;
  const ceiling = Math.floor(total * fraction);
  const caps = policyCaps(policy.data, f.policyBand, f.cityTier);
  const days = f.departureDate && f.returnDate ? Math.max(1, Math.round((new Date(f.returnDate) - new Date(f.departureDate)) / 86400000) + 1) : 0;
  const nights = Math.max(0, days - 1);
  const bandOptions = (policy.data?.bands || []).map((b) => b.band);
  const gradeUnconfirmed = me.data && !me.data.gradeValidated;

  const flags = useMemo(() => {
    const out = [];
    if (!caps || !days) return out;
    if (caps.hotel && nights && Number(f.costs.hotel) > caps.hotel * nights) out.push(`Hotel ${money(f.costs.hotel)} exceeds cap ${money(caps.hotel * nights)} (${nights} night${nights > 1 ? 's' : ''})`);
    if (caps.food !== null && caps.food !== undefined && Number(f.costs.food) > caps.food * days) out.push(`Food exceeds ${money(caps.food * days)} for ${days} day${days > 1 ? 's' : ''}`);
    if (Number(f.costs.ope) > (caps.ope || 0) * days) out.push(`OPE exceeds ${money((caps.ope || 0) * days)}`);
    return out;
  }, [caps, days, nights, f.costs]);

  const applyAllowances = () => {
    if (!caps || !days) { notify.info('Select policy band, city tier and dates first'); return; }
    setF((p) => ({
      ...p,
      costs: {
        ...p.costs,
        hotel: caps.hotel && nights ? caps.hotel * nights : p.costs.hotel,
        food: caps.food ? caps.food * days : p.costs.food,
        ope: caps.ope ? caps.ope * days : p.costs.ope,
      },
    }));
  };

  const save = async (submit) => {
    if (submit) {
      const missing = [['origin', 'Origin'], ['destination', 'Destination'], ['departureDate', 'Departure date'], ['returnDate', 'Return date'], ['purpose', 'Purpose of travel']].filter(([k]) => !f[k]).map(([, l]) => l);
      if (missing.length) { notify.error(`Please fill: ${missing.join(', ')}`); return; }
      if (new Date(f.returnDate) < new Date(f.departureDate)) { notify.error('Return date cannot be before departure date'); return; }
      if (!total) { notify.error('Enter the estimated costs'); return; }
    }
    setSaving(submit ? 'submit' : 'draft');
    try {
      const body = { ...f, costs: Object.fromEntries(COSTS.map(([k]) => [k, Number(f.costs[k]) || 0])), submit };
      const r = id ? await etamsAPI.updateTravel(id, body) : await etamsAPI.createTravel(body);
      notify.success(r.message || (submit ? 'Submitted' : 'Draft saved'));
      navigate(submit ? `/etams/travel-requests?id=${r.request._id}` : '/etams/travel-requests');
    } catch (e) {
      notify.error(e.message || 'Could not save');
    } finally {
      setSaving(null);
    }
  };

  if (id && existing.loading) return <PageShell section="Travel & Expenses"><Loading /></PageShell>;
  const t = existing.data;
  const readOnly = t && !['DRAFT', 'RETURNED'].includes(t.status);

  return (
    <PageShell section="Travel & Expenses">
      <PageHead
        title={t ? `Travel request ${t.requestNo || '(draft)'}` : 'New travel request'}
        subtitle="TSE-HR-TAE-001 · Proposed Rev. 01"
        actions={gradeUnconfirmed ? <span className="tse-chip-note"><i className="bi bi-exclamation-triangle-fill" style={{ color: '#d08a1c' }} />Legacy policy band requires HR confirmation</span> : null}
      />
      {readOnly && <Note tone="info" className="mb-18">This request is already {t.status.replace(/_/g, ' ').toLowerCase()} and can no longer be edited.</Note>}
      {t?.status === 'RETURNED' && <Note tone="warn" title="Returned for correction" className="mb-18">{(t.approvalHistory || []).filter((h) => h.action === 'RETURNED').slice(-1)[0]?.remarks}</Note>}

      <div className="tse-split wide-side">
        <div className="tse-stack">
          <div className="tse-card">
            <div className="tse-section-head">Employee &amp; assignment</div>
            <div className="tse-card-body">
              <div className="tse-form-grid c3">
                <Field label="Employee name"><Input value={user?.userName || ''} readOnly /></Field>
                <Field label="Employee ID"><Input value={user?.employeeCode || ''} readOnly /></Field>
                <Field label="Business unit"><Select value={f.businessUnit} onChange={(e) => set('businessUnit', e.target.value)} options={BUSINESS_UNITS} disabled={readOnly} /></Field>
                <Field label="Project">
                  <Select value={f.projectCode} onChange={(e) => set('projectCode', e.target.value)} placeholder="Select project" disabled={readOnly}
                    options={[...new Set([...(f.projectCode ? [f.projectCode] : []), ...projects.map((p) => p.projectCode)])].map((c) => ({ value: c, label: `${c}${projects.find((p) => p.projectCode === c)?.name ? ` — ${projects.find((p) => p.projectCode === c).name}` : ''}` }))} />
                </Field>
                <Field label="Policy band"><Select value={f.policyBand} onChange={(e) => set('policyBand', e.target.value)} placeholder="Select band" options={bandOptions} disabled={readOnly} /></Field>
                <Field label="City tier"><Select value={f.cityTier} onChange={(e) => set('cityTier', e.target.value)} options={[{ value: 'A', label: 'Tier A' }, { value: 'B', label: 'Tier B' }, { value: 'C', label: 'Tier C' }]} disabled={readOnly} /></Field>
              </div>
            </div>
          </div>

          <div className="tse-card">
            <div className="tse-section-head">Journey details</div>
            <div className="tse-card-body">
              <div className="tse-form-grid c4">
                <Field label="Origin" required><Input value={f.origin} onChange={(e) => set('origin', e.target.value)} placeholder="e.g. Jamshedpur" disabled={readOnly} /></Field>
                <Field label="Destination" required><Input value={f.destination} onChange={(e) => set('destination', e.target.value)} placeholder="e.g. West Bokaro" disabled={readOnly} /></Field>
                <Field label="Departure date" required><Input type="date" value={f.departureDate} onChange={(e) => set('departureDate', e.target.value)} disabled={readOnly} /></Field>
                <Field label="Return date" required><Input type="date" value={f.returnDate} min={f.departureDate || undefined} onChange={(e) => set('returnDate', e.target.value)} disabled={readOnly} /></Field>
                <Field label="Mode of travel"><Select value={f.modeOfTravel} onChange={(e) => set('modeOfTravel', e.target.value)} options={MODES_OF_TRAVEL} disabled={readOnly} /></Field>
                <Field label="Purpose of travel" required className="span-3"><Input value={f.purpose} onChange={(e) => set('purpose', e.target.value)} placeholder="e.g. Site execution and vendor coordination" disabled={readOnly} /></Field>
              </div>
            </div>
          </div>

          <div className="tse-card">
            <div className="tse-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Estimated costs</span>
              {!readOnly && <button type="button" className="tse-btn xs" style={{ background: 'rgba(255,255,255,.14)', borderColor: 'rgba(255,255,255,.3)' }} onClick={applyAllowances}><i className="bi bi-magic" />Apply policy allowances</button>}
            </div>
            <div className="tse-card-body">
              <div className="tse-form-grid c6">
                {COSTS.map(([k, l]) => (
                  <Field key={k} label={`${l} (₹)`}><Input type="number" min="0" value={f.costs[k]} onChange={(e) => setCost(k, e.target.value)} placeholder="0" disabled={readOnly} /></Field>
                ))}
              </div>
              {flags.length > 0 && <Note tone="warn" style={{ marginTop: 8 }} title="Above policy entitlement — justification may be requested">{flags.join(' · ')}</Note>}
            </div>
          </div>
          {!readOnly && (
            <div><button type="button" className="tse-btn lg outline" style={{ minWidth: 210 }} onClick={() => save(false)} disabled={!!saving}>{saving === 'draft' ? <Spin /> : null}Save draft</button></div>
          )}
        </div>

        <div className="tse-stack">
          <Card navy title="Cost summary">
            <div className="tse-muted">Estimated total</div>
            <div className="tse-big-amount">{money(total)}</div>
            <hr className="tse-divider" />
            <div className="tse-muted">Advance ceiling ({fraction === 1 / 3 ? '1/3' : `${Math.round(fraction * 100)}%`})</div>
            <div className="tse-big-amount dark" style={{ fontSize: '1.716rem' }}>{money(ceiling)}</div>
            <hr className="tse-divider" />
            {policy.loading ? <Loading /> : caps ? (
              <>
                <div className="tse-kv-row"><span className="k">Hotel base cap</span><span className="v">{money(caps.hotel)} / night</span></div>
                <div className="tse-kv-row"><span className="k">Food allowance</span><span className="v">{caps.food === null || caps.food === undefined ? 'At actuals' : `${money(caps.food)} / day`}</span></div>
                <div className="tse-kv-row"><span className="k">OPE allowance</span><span className="v">{money(caps.ope)} / day</span></div>
                {days > 0 && <div className="tse-kv-row"><span className="k">Duration</span><span className="v">{days} day{days > 1 ? 's' : ''} · {nights} night{nights !== 1 ? 's' : ''}</span></div>}
              </>
            ) : <div className="tse-muted tse-small">Select a policy band to see entitlements.</div>}
          </Card>
          <Card navy title="Approval flow">
            <VFlow items={[
              { mark: 1, title: 'Reporting manager', sub: 'Travel need and estimate' },
              { mark: 2, title: 'HOD', sub: 'Business approval' },
              { mark: 3, title: 'Administration', sub: 'Policy compliance and processing' },
            ]} />
          </Card>
          {!readOnly && <button type="button" className="tse-btn lg block" onClick={() => save(true)} disabled={!!saving}>{saving === 'submit' ? <Spin /> : null}Submit travel request</button>}
        </div>
      </div>
    </PageShell>
  );
}
